import { useGetQuery } from '../../graphql';
import {
  AssetRoutine,
  RoutineIdpState,
  RoutineIdpStatus,
  WorkflowRoutineState,
  isNonTerminalIdpStatus,
} from '../domain';

const IDP_POLL_INTERVAL_MS = 1500;

/** Shape of the react-query cache entry as `refetchInterval` sees it — the
 * raw, pre-`select` fetcher response (`{ getAssetRoutine: { data } }`), not
 * the unwrapped model `useGetQuery` returns to render code. */
interface GetAssetRoutineIdpQueryResult {
  getAssetRoutine?: Pick<AssetRoutine, 'data'>;
}

/**
 * Reads `idp.status` off the raw `getAssetRoutine.data` path — NEVER
 * `routineState.state.idp`. `upsertRoutineState` spreads `assetRoutine.data`
 * into `workspaceasset.routineState[].state` once, at create time, so that
 * copy is frozen at `PENDING` forever and would poll infinitely.
 */
export const getRawIdpStatus = (
  rawQueryData: unknown
): RoutineIdpStatus | undefined => {
  const assetRoutineData = (
    rawQueryData as GetAssetRoutineIdpQueryResult | undefined
  )?.getAssetRoutine?.data as WorkflowRoutineState | undefined;

  return assetRoutineData?.idp?.status;
};

/** `refetchInterval` decision: poll every 1500ms while non-terminal, stop on
 * a terminal status (or once there is no idp state at all — a non-IDP
 * routine, or the request hasn't resolved yet). */
export const getIdpPollInterval = (rawQueryData: unknown): number | false =>
  isNonTerminalIdpStatus(getRawIdpStatus(rawQueryData))
    ? IDP_POLL_INTERVAL_MS
    : false;

export interface UseAssetRoutineIdpPollResult {
  /** Full freeform `data` blob off the polled AssetRoutine — responses,
   * auditLog, and idp — so callers can re-render from fresh values once
   * extraction lands without a second fetch. */
  data: WorkflowRoutineState | undefined;
  idp: RoutineIdpState | undefined;
  isLoading: boolean;
}

export const useAssetRoutineIdpPoll = (
  assetRoutineId: string | undefined
): UseAssetRoutineIdpPollResult => {
  const { data, isLoading } = useGetQuery({
    modelName: 'assetRoutine',
    query: GET_ASSET_ROUTINE_IDP,
    // GetQueryArgs.id is typed as a required string, but the id is
    // legitimately undefined until the caller has it — same contract
    // mismatch as useCurrentAccountAvatar's `account?.id`. `enabled` below
    // keeps the query from firing until it's defined.
    id: assetRoutineId as string,
    options: {
      enabled: assetRoutineId !== undefined,
      refetchInterval: (query) => getIdpPollInterval(query?.state?.data),
    },
  });

  const assetRoutine = data as AssetRoutine | null;
  const workflowData = assetRoutine?.data as WorkflowRoutineState | undefined;

  return {
    data: workflowData,
    idp: workflowData?.idp,
    isLoading,
  };
};

const GET_ASSET_ROUTINE_IDP = `
  query GetAssetRoutineIdp($id: ID!) {
    getAssetRoutine(id: $id) {
      id
      data
    }
  }
`;
